import PropTypes from "prop-types";
import { Avatar, AvatarSize, Icon, IconNames } from "@wattpad/web-ui-library";
import { useTrans, useGetUser, useUserVerification } from "../hooks";
import { sendPollViewEvent } from "@wattpad/client-platform-web-events";
import { sprintf } from "sprintf-js";
import React, { useState, useEffect } from "react";
import classnames from "classnames";
import getReaderPoll from "../../helpers/polls/client.get-reader-poll";
import answerReaderPoll from "../../helpers/polls/client.answer-reader-poll";
import { sanitizeHTMLExceptQuotes } from "../helpers/sanitizeHTML";

const ReaderPollOption = ({
  pollData,
  option,
  isPollAuthor,
  onPollOptionClick,
  isMobile
}) => {
  const [optionWidth, setOptionWidth] = useState(100);

  useEffect(
    () => {
      // https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame
      // You need to wrap it to trigger the animation
      requestAnimationFrame(() => {
        // Set a new array of percentage widths based on the props
        setOptionWidth(option.answers?.percentageOfTotal);
      });
    },
    [option.answers?.percentageOfTotal]
  );

  return (
    <div
      className={classnames("poll-option", {
        interacted:
          pollData?.totalAnswers > 0 && (pollData?.interaction || isPollAuthor)
      })}
      style={{
        width:
          pollData?.totalAnswers > 0 && (pollData?.interaction || isPollAuthor)
            ? `${10 + optionWidth}%`
            : "100%",
        height: `${Math.floor(option.text.length / 65) * 20 + 50}px`
      }}
      key={option?.id}
      onClick={() => onPollOptionClick(option?.id)}
      role="button"
      tabIndex={0}
    >
      <div
        className="option-info-wrapper"
        style={{ height: `${Math.floor(option.text.length / 65) * 20 + 50}px` }}
      >
        <span
          className={classnames("poll-select", {
            interacted: pollData?.interaction
          })}
        >
          {option?.id === pollData?.interaction?.answer?.optionId &&
            !isPollAuthor && <Icon name={IconNames.WpCheck} size={16} />}
        </span>
        {option.text && (
          <div className={classnames("poll-option-text", { mobile: isMobile })}>
            {pollData?.totalAnswers > 0 &&
              (pollData?.interaction || isPollAuthor) &&
              `${optionWidth}% `}
            {sanitizeHTMLExceptQuotes(option.text)}
          </div>
        )}
      </div>
    </div>
  );
};

ReaderPollOption.propTypes = {
  option: PropTypes.object,
  pollData: PropTypes.object,
  isPollAuthor: PropTypes.bool,
  onPollOptionClick: PropTypes.func,
  isMobile: PropTypes.bool
};

const ReaderPoll = ({ storyId, partId, writerPollData, isMobile }) => {
  const { ngettext, trans } = useTrans();
  const currentUser = useGetUser().username;
  const errorMessage = trans("Something went wrong. Please try again");
  const [isPollAuthor, setIsPollAuthor] = useState();
  const { userNeedsVerification } = useUserVerification();

  const [pollData, setPollData] = useState();

  useEffect(() => {
    if (writerPollData) {
      setPollData(writerPollData);
      return;
    }
    getReaderPoll(storyId, partId).then(data => {
      const poll = data?.polls[0];
      setPollData(poll);
      setIsPollAuthor(currentUser === poll?.owner?.username);
      sendPollViewEvent(poll?.id, "story_part", partId, !!poll?.interaction);
    });
  }, []);

  const onPollOptionClick = optionId => {
    if (userNeedsVerification()) return;

    if (isPollAuthor || pollData.interaction) return;

    answerReaderPoll(storyId, partId, pollData.id, optionId)
      .then(data => {
        setPollData(data);
      })
      .catch(() => {
        wattpad.utils.showToast(errorMessage, { type: "dismissable" });
      });
  };

  return !pollData ? (
    <></>
  ) : (
    <div className="reader-poll-container">
      <div className={classnames("poll-author", { mobile: isMobile })}>
        <Avatar
          ariaLabel={pollData?.owner?.username}
          src={pollData?.owner?.avatar}
          size={isMobile ? AvatarSize.SMALL : AvatarSize.COMMENT_CARD_AVATAR}
        />
        {pollData?.owner?.username}
      </div>
      <div className={classnames("poll-question", { mobile: isMobile })}>
        {sanitizeHTMLExceptQuotes(pollData?.question)}
      </div>
      {pollData?.options.map(option => (
        <ReaderPollOption
          pollData={pollData}
          option={option}
          isPollAuthor={isPollAuthor}
          onPollOptionClick={onPollOptionClick}
          isMobile={isMobile}
          key={option.id}
        />
      ))}
      <div className={classnames("info-text", { mobile: isMobile })}>
        {pollData?.totalAnswers > 0
          ? sprintf(
              ngettext("%s response", "%s responses", pollData?.totalAnswers),
              pollData?.totalAnswers
            )
          : trans("No responses yet")}
        {!isPollAuthor && (
          <>
            <span className="info-dot" />
            {trans("Your response is anonymous")}
          </>
        )}
      </div>
    </div>
  );
};

ReaderPoll.propTypes = {
  storyId: PropTypes.number,
  partId: PropTypes.number,
  writerPollData: PropTypes.object,
  isMobile: PropTypes.bool
};

export default ReaderPoll;
